import { type FC, type PropsWithChildren, useMemo } from 'react';

import { CmsProvider, type ComponentsDefinition } from '@eneco/cms';
import { createSitecoreAdapter } from '@eneco/cms-sitecore';
import env from '@eneco/common/env';
import { CmsEditingEnhancer, CmsEnhancer } from '@sitecore/common';
import type { LayoutServiceData } from '@sitecore/types/lib';

let cmsAdapter: ReturnType<typeof createSitecoreAdapter> | undefined;

const getCmsAdapter = () => {
  if (!cmsAdapter) {
    cmsAdapter = createSitecoreAdapter({
      layoutServiceUrl: `${env('SITECORE_API_HOST')}${env('SITECORE_LAYOUT_SERVICE_ROUTE')}`,
      apiKey: env('SITECORE_API_KEY'),
    });
  }
  return cmsAdapter;
};

interface SitecoreCmsProviderProps {
  layoutData: LayoutServiceData;
  components: ComponentsDefinition;
}

/**
 * Mounts a `CmsProvider` (backed by the Sitecore adapter) for the current
 * route, so bridge components rendered in any Sitecore container can use
 * `useCms`/`useContent`. When there is no route (error/404/500 states) it
 * renders children unwrapped, so `toPage` never runs on a partial layout.
 */
export const SitecoreCmsProvider: FC<PropsWithChildren<SitecoreCmsProviderProps>> = ({
  layoutData,
  components,
  children,
}) => {
  const adapter = getCmsAdapter();
  const route = layoutData?.sitecore?.route;
  const page = useMemo(() => (route ? adapter.toPage(layoutData) : undefined), [route, adapter, layoutData]);

  if (!page) return <>{children}</>;

  return (
    <CmsProvider
      adapter={adapter}
      page={page}
      components={components}
      // A component rendered through the `@eneco/cms` `Placeholder` gets the tracking and next best action
      // wrappers Sitecore's own placeholder gives it, as on the CMS lane, so a slot renders the same through either.
      // In edit and preview mode Sitecore's placeholder leaves the next best action feedback out, and so does this.
      wrapComponent={page.context.pageEditing ? CmsEditingEnhancer : CmsEnhancer}>
      {children}
    </CmsProvider>
  );
};
